import { Loader2, AlertTriangle, Coins, CalendarRange } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useTalentDiscoveryMeter } from '@/hooks/useTalentDiscoveryMeter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { formatLocalizedDate } from '@/lib/localizedDate';

interface TalentDiscoveryMeterProps {
  variant?: 'compact' | 'card';
  className?: string;
}

export function TalentDiscoveryMeter({ variant = 'compact', className }: TalentDiscoveryMeterProps) {
  const { t, i18n } = useTranslation();
  const { enabled: flagOn } = useFeatureFlag('talent_discovery_governance_v2');
  const { meter, isLoading } = useTalentDiscoveryMeter(flagOn);

  if (!flagOn) return null;

  if (isLoading || !meter) {
    return (
      <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>{t('hire.talent.meter.loading')}</span>
      </div>
    );
  }

  const monthlyPct = meter.monthly_included > 0
    ? Math.min(100, Math.round((meter.monthly_consumed / meter.monthly_included) * 100))
    : 0;
  const creditsPct = meter.credits_purchased > 0
    ? Math.min(100, Math.round((meter.credits_consumed / meter.credits_purchased) * 100))
    : 0;

  const exhausted = !meter.can_send;
  const usingCredits = meter.monthly_remaining === 0 && meter.credits_remaining > 0;

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'rounded-lg border p-3 space-y-2 text-xs',
          exhausted ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30',
          className,
        )}
        data-testid="talent-discovery-meter-compact"
      >
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground inline-flex items-center gap-1.5">
            <CalendarRange className="w-3.5 h-3.5" />
            {t('hire.talent.meter.monthlyInvitations')}
          </span>
          <span className="tabular-nums text-muted-foreground">
            {meter.monthly_consumed} / {meter.monthly_included}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={cn('h-full transition-all', monthlyPct >= 100 ? 'bg-destructive' : 'bg-primary')}
            style={{ width: `${monthlyPct}%` }}
          />
        </div>

        {meter.credits_purchased > 0 && (
          <>
            <div className="flex items-center justify-between pt-1">
              <span className="font-medium text-foreground inline-flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" />
                {t('hire.talent.meter.purchasedCredits')}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {meter.credits_consumed} / {meter.credits_purchased}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={cn('h-full transition-all', creditsPct >= 100 ? 'bg-destructive' : 'bg-warning')}
                style={{ width: `${creditsPct}%` }}
              />
            </div>
          </>
        )}

        {exhausted && (
          <div className="flex items-start gap-1.5 text-destructive pt-1">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{t('hire.talent.meter.quotaExhausted')}</span>
          </div>
        )}
        {!exhausted && usingCredits && (
          <p className="text-warning pt-0.5">{t('hire.talent.meter.usingCredits')}</p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border border-border bg-card p-4 space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground text-sm">{t('hire.talent.meter.title')}</h3>
        {meter.period_end && (
          <span className="text-[10px] text-muted-foreground">
            {t('hire.talent.meter.resets', { date: formatLocalizedDate(meter.period_end, 'MMM d', i18n.language) })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t('hire.talent.meter.monthly')}</p>
          <p className="text-2xl font-bold tabular-nums text-foreground">
            {meter.monthly_remaining}
            <span className="text-sm font-normal text-muted-foreground"> / {meter.monthly_included}</span>
          </p>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2">
            <div
              className={cn('h-full', monthlyPct >= 100 ? 'bg-destructive' : 'bg-primary')}
              style={{ width: `${monthlyPct}%` }}
            />
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t('hire.talent.meter.credits')}</p>
          <p className="text-2xl font-bold tabular-nums text-foreground">
            {meter.credits_remaining}
            <span className="text-sm font-normal text-muted-foreground"> / {meter.credits_purchased}</span>
          </p>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2">
            <div
              className={cn('h-full', creditsPct >= 100 ? 'bg-destructive' : 'bg-warning')}
              style={{ width: `${creditsPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="pt-1 text-xs text-muted-foreground">
        {t('hire.talent.meter.totalAvailable')}: <span className="font-semibold text-foreground tabular-nums">{meter.total_remaining}</span>
        {exhausted && <span className="text-destructive ms-2">{t('hire.talent.meter.exhaustedSuffix')}</span>}
      </div>
    </div>
  );
}
